import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [tabletCollapsed, setTabletCollapsed] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  // ── SINGLE EXCLUSIVE ACCORDION STATE ─────────────────────────────────────
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);

  // Refs for auto-scroll after accordion opens
  const menuRefs = useRef({});
  const navRef = useRef(null);

  const toggleMenu = (menu) => {
    const next = openMenu === menu ? null : menu;
    setOpenMenu(next);
    if (next) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          menuRefs?.current?.[next]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        });
      });
    }
  };

  const toggleMobileMenu = (menu) => {
    setMobileOpenMenu(prev => prev === menu ? null : menu);
  };

  const userMenuRef = useRef(null);

  // ── Top-level standalone item ──────────────────────────────────────────────
  const dashboardItem = { label: 'Dashboard General', path: '/dashboard-general', icon: 'LayoutDashboard', roles: ['admin'] };

  // ── 2. SALA DE CORTE ──────────────────────────────────────────────────────
  const salaItems = [
    { label: 'Dashboard Sala', path: '/dashboard-sala-corte', icon: 'Gauge', roles: ['admin'] },
    { label: 'Plan de Trabajo', path: '/plan-trabajo', icon: 'CalendarCheck', roles: ['admin', 'employee'] },
    { label: 'Registro de Piezas', path: '/piece-registration-form', icon: 'FileText', roles: ['admin', 'employee'] },
    { label: 'Control de Loncheados', path: '/loncheados-control-center', icon: 'BarChart3', roles: ['admin', 'employee'] },
    { label: 'Trazabilidad', path: '/trazabilidad', icon: 'GitBranch', roles: ['admin', 'employee'] },
    { label: 'Catálogo de Piezas', path: '/product-management-simple', icon: 'Box', roles: ['admin'] },
    { label: 'Clientes Sala', path: '/clientes', icon: 'Users', roles: ['admin'] },
    { label: 'Operarios', path: '/employee-management', icon: 'UserCircle', roles: ['admin'] },
    { label: 'Salarios de Corte', path: '/salarios', icon: 'Coins', roles: ['admin'] },
    { label: 'Rentabilidad Sala', path: '/client-profitability-analysis', icon: 'TrendingUp', roles: ['admin'] },
    { label: 'Calendario de Servicios', path: '/calendario-eventos', icon: 'CalendarDays', roles: ['admin'] },
  ];

  // ── 3. TIENDA ─────────────────────────────────────────────────────────────
  const tiendaItems = [
    { label: 'Dashboard Tienda', path: '/tienda-general', icon: 'LayoutDashboard', roles: ['admin'] },
    { label: 'Ventas Epelsa', path: '/tienda-ventas', icon: 'TrendingUp', roles: ['admin'] },
    { label: 'Caja Diaria', path: '/tienda-caja-diaria', icon: 'Wallet', roles: ['admin'] },
    { label: 'Visor / TPV Epelsa', path: '/tienda-tpv-fisico', icon: 'Monitor', roles: ['admin'] },
    { label: 'Pedidos Web', path: '/tienda-pedidos-web', icon: 'ShoppingCart', roles: ['admin'] },
    { label: 'Analítica Tienda', path: '/tienda-analitica', icon: 'BarChart2', roles: ['admin'] },
    { label: 'APPCC / Autocontrol', path: '/tienda-appcc', icon: 'ShieldCheck', roles: ['admin'] },
  ];

  // ── 4. DISTRIBUCIÓN ───────────────────────────────────────────────────────
  const distItems = [
    { label: 'Dashboard Distribución', path: '/distribucion', icon: 'LayoutDashboard', roles: ['admin'] },
    { label: 'Clientes', path: '/dist-clientes', icon: 'UserCircle', roles: ['admin'] },
    { label: 'Pedidos', path: '/dist-pedidos', icon: 'ShoppingCart', roles: ['admin'] },
    { label: 'Preparación de Pedidos', path: '/dist-preparacion', icon: 'PackageCheck', roles: ['admin'] },
    { label: 'Rutas / Entregas', path: '/dist-rutas', icon: 'Route', roles: ['admin'] },
    { label: 'Analítica Distribución', path: '/dist-analitica', icon: 'BarChart2', roles: ['admin'] },
  ];

  // ── 5. PRODUCTOS Y ALMACÉN ────────────────────────────────────────────────
  const almacenItems = [
    { label: 'Productos', path: '/tienda-productos', icon: 'Box', roles: ['admin'] },
    { label: 'Inventario', path: '/tienda-inventario', icon: 'Package', roles: ['admin'] },
    { label: 'Movimientos', path: '/tienda-movimientos', icon: 'ArrowUpDown', roles: ['admin'] },
    { label: 'Tarifas y Precios', path: '/precios', icon: 'Tag', roles: ['admin'] },
    { label: 'Proveedores', path: '/tienda-proveedores', icon: 'Truck', roles: ['admin'] },
  ];

  // ── 6. COMPRAS Y FACTURACIÓN ──────────────────────────────────────────────
  const comprasItems = [
    // COMPRAS
    { label: 'Compras', path: '/tienda-compras', icon: 'ShoppingBag', roles: ['admin'] },
    { label: 'Facturas Recibidas', path: '/tienda-facturas-entrantes', icon: 'FileInput', roles: ['admin'] },
    { label: 'Importar Factura / OCR', path: '/compras-importar', icon: 'ScanLine', roles: ['admin'] },
    { label: 'Bandeja Email', path: '/compras-bandeja-email', icon: 'Mail', roles: ['admin'] },
    // FACTURACIÓN
    { label: 'Facturas Emitidas', path: '/fin-facturacion', icon: 'FileOutput', roles: ['admin'] },
    { label: 'Albaranes', path: '/fin-albaranes', icon: 'FileCheck', roles: ['admin'] },
    // OPERACIONES
    { label: 'Cobros', path: '/dist-cobros', icon: 'ArrowDownCircle', roles: ['admin'] },
    { label: 'Pagos', path: '/dist-pagos', icon: 'ArrowUpCircle', roles: ['admin'] },
  ];

  // ── 7. FINANZAS ───────────────────────────────────────────────────────────
  const finItems = [
    { label: 'Dashboard Financiero', path: '/finanzas-corporativas', icon: 'LayoutDashboard', roles: ['admin'] },
    { label: 'Contabilidad', path: '/fin-contabilidad', icon: 'BookOpen', roles: ['admin'] },
    { label: 'Tesorería', path: '/fin-tesoreria', icon: 'Wallet', roles: ['admin'] },
    { label: 'Bancos y Conciliación', path: '/fin-bancos', icon: 'Landmark', roles: ['admin'] },
    { label: 'Gastos', path: '/fin-gastos', icon: 'Receipt', roles: ['admin'] },
    { label: 'Centros de Coste', path: '/fin-centros-coste', icon: 'PieChart', roles: ['admin'] },
    { label: 'Auditoría', path: '/auditoria-total', icon: 'Shield', roles: ['admin'] },
  ];

  const currentUser = {
    name: userProfile?.nombre_completo || user?.email?.split('@')?.[0] || 'Usuario',
    role: userProfile?.rol || 'admin',
    email: user?.email || 'usuario@lasdosdoncellas.com',
    initials: (userProfile?.nombre_completo || user?.email || 'U')?.charAt(0)?.toUpperCase(),
  };

  const visibleSalaItems = salaItems?.filter(item => item?.roles?.includes(currentUser?.role));
  const visibleTiendaItems = tiendaItems?.filter(item => item?.roles?.includes(currentUser?.role));
  const visibleDistItems = distItems?.filter(item => item?.roles?.includes(currentUser?.role));
  const visibleAlmacenItems = almacenItems?.filter(item => item?.roles?.includes(currentUser?.role));
  const visibleComprasItems = comprasItems?.filter(item => item?.roles?.includes(currentUser?.role));
  const visibleFinItems = finItems?.filter(item => item?.roles?.includes(currentUser?.role));

  const isSalaActive = visibleSalaItems?.some(item => location?.pathname === item?.path || location?.pathname?.startsWith('/clientes'));
  const isTiendaActive = visibleTiendaItems?.some(item => location?.pathname === item?.path);
  const isDistActive = visibleDistItems?.some(item => location?.pathname === item?.path);
  const isAlmacenActive = visibleAlmacenItems?.some(item => location?.pathname === item?.path);
  const isComprasActive = visibleComprasItems?.some(item => location?.pathname === item?.path);
  const isFinActive = visibleFinItems?.some(item => location?.pathname === item?.path);
  const isIAActive = location?.pathname === '/ia-empresarial';

  const collapsed = tabletCollapsed;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(e?.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try { if (signOut) await signOut(); } catch {}
    navigate('/login');
    setUserMenuOpen(false);
  };

  const isActive = (path) => location?.pathname === path;

  // ─── REUSABLE NAV BUTTON ─────────────────────────────────────────────────────
  const NavButton = ({ item, indent }) =>
    <button
      onClick={() => handleNavigation(item?.path)}
      title={collapsed ? item?.label : undefined}
      className={`w-full flex items-center gap-2.5 rounded-md font-oranienbaum transition-all duration-150 text-left group relative
          ${indent && !collapsed ? 'pl-7' : 'px-3'}
          ${isActive(item?.path)
            ? 'bg-[#D6EF4E]/12 text-[#D6EF4E] border border-[#D6EF4E]/20 shadow-sm'
            : 'text-white/70 hover:bg-white/6 hover:text-white/90 border border-transparent'}
          ${collapsed ? 'justify-center py-2' : ''}
        `}
      style={{ fontSize: '0.8125rem', fontWeight: 500, minHeight: '34px', paddingTop: collapsed ? undefined : '0.4rem', paddingBottom: collapsed ? undefined : '0.4rem' }}>
      {isActive(item?.path) &&
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-[#D6EF4E] rounded-r-full" />
      }
      <Icon
        name={item?.icon}
        size={15}
        className={`flex-shrink-0 ${isActive(item?.path) ? 'text-[#D6EF4E]' : 'text-white/55 group-hover:text-white/85'}`} />
      {!collapsed && <span className="truncate text-lg font-thin">{item?.label}</span>}
      {collapsed &&
        <span className="absolute left-full ml-2 px-2 py-1 bg-[#1A1A1A] text-white rounded border border-white/10 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50"
          style={{ fontSize: '0.75rem' }}>
          {item?.label}
        </span>
      }
    </button>;

  // ─── COLLAPSIBLE GROUP (EXCLUSIVE ACCORDION) ─────────────────────────────────
  const CollapsibleGroup = ({ menuKey, emoji, label, isGroupActive, items, accentColor = '#D6EF4E' }) => {
    const isOpen = openMenu === menuKey;
    return (
      <div ref={el => { menuRefs.current[menuKey] = el; }}>
        <button
          onClick={() => toggleMenu(menuKey)}
          className={`w-full flex items-center gap-2.5 px-3 rounded-md font-oranienbaum transition-all duration-200 text-left group border text-lg
              ${isGroupActive
                ? `border-transparent`
                : 'text-white/75 hover:bg-white/6 hover:text-white/90 border-transparent'}`
          }
          style={Object.assign(
            { fontSize: '0.875rem', fontWeight: 600, minHeight: '38px', paddingTop: '0.45rem', paddingBottom: '0.45rem' },
            isGroupActive ? { background: `${accentColor}14`, color: accentColor, borderColor: `${accentColor}33` } : {}
          )}>
          <span className="text-sm flex-shrink-0">{emoji}</span>
          <span className="flex-1 truncate text-lg" style={isGroupActive ? { color: accentColor } : { color: 'rgba(255,255,255,0.75)' }}>{label}</span>
          <Icon
            name={isOpen ? 'ChevronDown' : 'ChevronRight'}
            size={13}
            className={`flex-shrink-0 transition-transform duration-200 ${isGroupActive ? 'opacity-60' : 'text-white/35'}`} />
        </button>
        {/* Submenu */}
        <div
          style={{
            maxHeight: isOpen ? '2000px' : '0px',
            overflow: 'hidden',
            transition: isOpen ? 'max-height 0.35s ease' : 'max-height 0.25s ease',
          }}>
          <div className="ml-2 mt-0.5 space-y-0 border-l pl-2" style={{ borderColor: `${accentColor}25` }}>
            {items?.map(item =>
              <NavButton key={item?.path} item={item} indent={false} />
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── MOBILE HEADER ───────────────────────────────────────────────────────────
  const MobileHeader = () =>
    <header className="lg:hidden mobile-header-only fixed top-0 left-0 right-0 bg-[#0A0A0A] border-b border-white/10 shadow-md z-[1000] flex items-center justify-between px-4"
      style={{ height: '3.75rem' }}>
      <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleNavigation('/dashboard-general')}>
        <img
          src="/assets/images/logo_las_dos_doncellas_1MB-1784658867185.jpg"
          alt="Las Dos Doncellas"
          className="h-10 w-10 object-contain rounded" />
        <span className="font-oranienbaum font-bold text-[#D6EF4E]" style={{ fontSize: 'var(--fs-menu-main)' }}>Las Dos Doncellas</span>
      </div>
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors">
        <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
      </button>
    </header>;

  // ─── MOBILE NAV BUTTON ────────────────────────────────────────────────────────
  const MobileNavBtn = ({ item }) =>
    <button
      onClick={() => handleNavigation(item?.path)}
      className={`w-full flex items-center gap-3 px-4 rounded-lg font-oranienbaum transition-colors text-left ${isActive(item?.path)
        ? 'bg-[#D6EF4E]/15 text-[#D6EF4E] border border-[#D6EF4E]/30'
        : 'text-white/70 hover:bg-white/8 hover:text-white'}`}
      style={{ fontSize: 'var(--fs-menu-sub)', fontWeight: 500, minHeight: 'var(--sidebar-subitem-height)', paddingTop: '0.75rem', paddingBottom: '0.75rem' }}>
      <Icon name={item?.icon} size={19} />
      {item?.label}
    </button>;

  // ─── MOBILE COLLAPSIBLE GROUP ──────────────────────────────────────────────────
  const MobileGroup = ({ menuKey, emoji, label, items, accentColor = '#D6EF4E' }) => {
    const isOpen = mobileOpenMenu === menuKey;
    return (
      <div>
        <button
          onClick={() => toggleMobileMenu(menuKey)}
          className="w-full flex items-center gap-3 px-4 rounded-lg font-oranienbaum transition-colors text-left text-white/75 hover:bg-white/8 hover:text-white"
          style={{ fontSize: 'var(--fs-menu-main)', fontWeight: 600, minHeight: 'var(--sidebar-item-height)', paddingTop: '0.875rem', paddingBottom: '0.875rem' }}>
          <span className="text-lg">{emoji}</span>
          <span className="flex-1">{label}</span>
          <Icon name={isOpen ? 'ChevronDown' : 'ChevronRight'} size={16} className="text-white/40" />
        </button>
        <div
          style={{
            maxHeight: isOpen ? '2000px' : '0px',
            overflow: 'hidden',
            transition: isOpen ? 'max-height 0.35s ease' : 'max-height 0.25s ease',
          }}>
          <div className="ml-4 mt-0.5 space-y-0.5 pl-3" style={{ borderLeft: `1px solid ${accentColor}20` }}>
            {items?.map(item => <MobileNavBtn key={item?.path} item={item} />)}
          </div>
        </div>
      </div>
    );
  };

  // ─── MOBILE OVERLAY MENU ─────────────────────────────────────────────────────
  const MobileMenu = () =>
    <>
      {mobileMenuOpen &&
        <div className="lg:hidden fixed inset-0 z-[999]">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <div
            className="absolute top-[3.75rem] left-0 bottom-0 bg-[#0A0A0A] border-r border-white/10 flex flex-col"
            style={{ width: 'min(88vw, 360px)' }}>
            <nav className="flex-1 overflow-y-auto p-3 space-y-1" style={{ minHeight: 0, overscrollBehavior: 'contain', paddingBottom: '24px' }}>
              <button
                onClick={() => handleNavigation(dashboardItem?.path)}
                className={`w-full flex items-center gap-3 px-4 rounded-lg font-oranienbaum transition-colors text-left ${isActive(dashboardItem?.path)
                  ? 'bg-[#D6EF4E]/15 text-[#D6EF4E] border border-[#D6EF4E]/30'
                  : 'text-white/75 hover:bg-white/8 hover:text-white'}`}
                style={{ fontSize: 'var(--fs-menu-main)', fontWeight: 600, minHeight: 'var(--sidebar-item-height)', paddingTop: '0.875rem', paddingBottom: '0.875rem' }}>
                <Icon name={dashboardItem?.icon} size={21} />
                {dashboardItem?.label}
              </button>
              <MobileGroup menuKey="sala" emoji="🔪" label="Sala de Corte" items={visibleSalaItems} accentColor="#D6EF4E" />
              <MobileGroup menuKey="tienda" emoji="🏪" label="Tienda" items={visibleTiendaItems} accentColor="#34D399" />
              <MobileGroup menuKey="dist" emoji="🚚" label="Distribución" items={visibleDistItems} accentColor="#60A5FA" />
              <MobileGroup menuKey="almacen" emoji="📦" label="Productos y Almacén" items={visibleAlmacenItems} accentColor="#F59E0B" />
              <MobileGroup menuKey="compras" emoji="🧾" label="Compras y Facturación" items={visibleComprasItems} accentColor="#A78BFA" />
              <MobileGroup menuKey="fin" emoji="💰" label="Finanzas" items={visibleFinItems} accentColor="#D6EF4E" />
              <button
                onClick={() => handleNavigation('/ia-empresarial')}
                className={`w-full flex items-center gap-3 px-4 rounded-lg font-oranienbaum transition-colors text-left ${isActive('/ia-empresarial')
                  ? 'bg-[#A78BFA]/15 text-[#A78BFA] border border-[#A78BFA]/30' : 'text-white/75 hover:bg-white/8 hover:text-white'}`}
                style={{ fontSize: 'var(--fs-menu-main)', fontWeight: 600, minHeight: 'var(--sidebar-item-height)', paddingTop: '0.875rem', paddingBottom: '0.875rem' }}>
                <span className="text-lg">🤖</span>
                <span>IA Empresarial</span>
              </button>
            </nav>
            <div className="flex-shrink-0 p-3 border-t border-white/10 space-y-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 rounded-lg font-oranienbaum text-red-400 hover:bg-red-500/10 transition-colors text-left"
                style={{ fontSize: 'var(--fs-menu-sub)', minHeight: 'var(--sidebar-subitem-height)', paddingTop: '0.75rem', paddingBottom: '0.75rem' }}>
                <Icon name="LogOut" size={20} />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      }
    </>;

  return (
    <>
      <MobileHeader />
      <MobileMenu />
      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        className={`hidden lg:flex desktop-sidebar-only flex-col fixed top-0 left-0 z-[900] transition-all duration-300 border-r border-white/8`}
        style={{
          width: collapsed ? '72px' : 'var(--sidebar-width)',
          height: '100dvh',
          background: 'linear-gradient(180deg, #0D0D0D 0%, #111111 40%, #0A0A0A 100%)',
          boxShadow: '2px 0 20px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}>

        {/* ── ZONE 1: FIXED HEADER — Logo + Name ── */}
        <div
          style={{
            flex: '0 0 auto',
            paddingLeft: '10px',
            paddingRight: '8px',
            paddingTop: '8px',
            paddingBottom: '8px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}>
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 cursor-pointer" onClick={() => handleNavigation('/dashboard-general')}>
              <img
                src="/assets/images/logo_las_dos_doncellas_1MB-1784658867185.jpg"
                alt="Las Dos Doncellas"
                className="object-contain rounded-md"
                style={{ width: collapsed ? '132px' : '144px', height: collapsed ? '132px' : '144px' }} />
            </div>
            {!collapsed &&
              <div className="flex flex-col justify-center min-w-0 cursor-pointer" onClick={() => handleNavigation('/dashboard-general')}>
                <p className="font-oranienbaum font-bold text-[#D6EF4E] whitespace-nowrap ml-[-37px] text-[21px]" style={{ fontSize: 'var(--fs-menu-main)' }}>
                  Las Dos Doncellas
                </p>
                <p className="font-oranienbaum text-white/60 leading-tight whitespace-nowrap ml-[-15px]" style={{ fontSize: 'var(--fs-sm)' }}>ERP Empresarial</p>
              </div>
            }
            {/* Tablet collapse toggle */}
            <button
              onClick={() => setTabletCollapsed(!collapsed)}
              className="xl:hidden flex-shrink-0 ml-auto p-1.5 rounded text-white/35 hover:text-white/65 transition-colors">
              <Icon name={collapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} />
            </button>
          </div>
        </div>

        {/* ── ZONE 2: SCROLLABLE NAVIGATION ── */}
        <nav
          ref={navRef}
          className="py-2 px-2 space-y-0 scrollbar-custom"
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            paddingBottom: '24px',
          }}>

          {/* 1. Dashboard General */}
          <NavButton item={dashboardItem} indent={false} />

          {!collapsed ? (
            <>
              {/* 2. Sala de Corte */}
              <CollapsibleGroup
                menuKey="sala"
                emoji="🔪"
                label="Sala de Corte"
                isGroupActive={isSalaActive}
                items={visibleSalaItems}
                accentColor="#D6EF4E" />

              {/* 3. Tienda */}
              <CollapsibleGroup
                menuKey="tienda"
                emoji="🏪"
                label="Tienda"
                isGroupActive={isTiendaActive}
                items={visibleTiendaItems}
                accentColor="#34D399" />

              {/* 4. Distribución */}
              <CollapsibleGroup
                menuKey="dist"
                emoji="🚚"
                label="Distribución"
                isGroupActive={isDistActive}
                items={visibleDistItems}
                accentColor="#60A5FA" />

              {/* 5. Productos y Almacén */}
              <CollapsibleGroup
                menuKey="almacen"
                emoji="📦"
                label="Productos y Almacén"
                isGroupActive={isAlmacenActive}
                items={visibleAlmacenItems}
                accentColor="#F59E0B" />

              {/* 6. Compras y Facturación */}
              <CollapsibleGroup
                menuKey="compras"
                emoji="🧾"
                label="Compras y Facturación"
                isGroupActive={isComprasActive}
                items={visibleComprasItems}
                accentColor="#A78BFA" />

              {/* 7. Finanzas */}
              <CollapsibleGroup
                menuKey="fin"
                emoji="💰"
                label="Finanzas"
                isGroupActive={isFinActive}
                items={visibleFinItems}
                accentColor="#D6EF4E" />

              {/* 8. IA Empresarial */}
              <button
                onClick={() => handleNavigation('/ia-empresarial')}
                className={`w-full flex items-center gap-2.5 px-3 rounded-md font-oranienbaum transition-all duration-150 text-left group relative border mt-0.5
                    ${isIAActive
                      ? 'bg-[#A78BFA]/12 text-[#A78BFA] border-[#A78BFA]/20 shadow-sm'
                      : 'text-white/75 hover:bg-white/6 hover:text-white/90 border-transparent'}`
                }
                style={{ fontSize: '0.875rem', fontWeight: 600, minHeight: '38px', paddingTop: '0.45rem', paddingBottom: '0.45rem' }}>
                {isIAActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-[#A78BFA] rounded-r-full" />}
                <span className="text-sm flex-shrink-0">🤖</span>
                <span className="truncate text-[17px]">IA Empresarial</span>
              </button>
            </>
          ) : (
            /* Collapsed: icon-only */
            (<div className="mt-1 space-y-0.5">
              <div className="w-full flex items-center justify-center py-1 text-white/20 text-base">🔪</div>
              {visibleSalaItems?.slice(0, 3)?.map(item => <NavButton key={item?.path} item={item} indent={false} />)}
              <div className="my-1 border-t border-white/8" />
              <div className="w-full flex items-center justify-center py-1 text-white/20 text-base">🏪</div>
              {visibleTiendaItems?.slice(0, 3)?.map(item => <NavButton key={item?.path} item={item} indent={false} />)}
              <div className="my-1 border-t border-white/8" />
              <div className="w-full flex items-center justify-center py-1 text-white/20 text-base">🚚</div>
              {visibleDistItems?.slice(0, 3)?.map(item => <NavButton key={item?.path} item={item} indent={false} />)}
              <div className="my-1 border-t border-white/8" />
              <div className="w-full flex items-center justify-center py-1 text-white/20 text-base">📦</div>
              {visibleAlmacenItems?.slice(0, 3)?.map(item => <NavButton key={item?.path} item={item} indent={false} />)}
              <div className="my-1 border-t border-white/8" />
              <div className="w-full flex items-center justify-center py-1 text-white/20 text-base">🧾</div>
              {visibleComprasItems?.slice(0, 3)?.map(item => <NavButton key={item?.path} item={item} indent={false} />)}
              <div className="my-1 border-t border-white/8" />
              <div className="w-full flex items-center justify-center py-1 text-white/20 text-base">💰</div>
              {visibleFinItems?.slice(0, 3)?.map(item => <NavButton key={item?.path} item={item} indent={false} />)}
              <div className="my-1 border-t border-white/8" />
              <button
                onClick={() => handleNavigation('/ia-empresarial')}
                title="IA Empresarial"
                className="w-full flex items-center justify-center py-2 text-white/20 text-base hover:text-white/50 transition-colors">
                🤖
              </button>
            </div>)
          )}
        </nav>

        {/* ── ZONE 3: FIXED FOOTER — Configuración + Usuario ── */}
        <div
          style={{
            flex: '0 0 auto',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            padding: '8px',
          }}
          className="space-y-1">

          {/* 9. Configuración */}
          <button
            onClick={() => handleNavigation('/ia-empresarial')}
            title={collapsed ? 'Configuración' : undefined}
            className={`w-full flex items-center gap-2.5 px-3 rounded-md font-oranienbaum text-white/60 hover:bg-white/6 hover:text-white/85 transition-colors border border-transparent group relative
              ${collapsed ? 'justify-center' : ''}`}
            style={{ fontSize: '0.8125rem', fontWeight: 500, height: '36px' }}>
            <Icon name="Settings" size={15} className="flex-shrink-0 text-white/50 group-hover:text-white/80" />
            {!collapsed && <span>Configuración</span>}
            {collapsed &&
              <span className="absolute left-full ml-2 px-2 py-1 bg-[#1A1A1A] text-white rounded border border-white/10 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50"
                style={{ fontSize: '0.75rem' }}>
                Configuración
              </span>
            }
          </button>

          {/* User Profile */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              onMouseEnter={() => setShowEmail(true)}
              onMouseLeave={() => setShowEmail(false)}
              title={collapsed ? currentUser?.name : undefined}
              className={`w-full flex items-center gap-2.5 px-3 rounded-md font-oranienbaum text-white/75 hover:bg-white/6 hover:text-white/90 transition-colors border border-transparent group
                ${collapsed ? 'justify-center' : ''}`}
              style={{ height: '50px' }}>
              <div className="w-8 h-8 rounded-full bg-[#D6EF4E]/20 border border-[#D6EF4E]/30 flex items-center justify-center flex-shrink-0"
                style={{ minWidth: '32px', minHeight: '32px', width: '32px', height: '32px' }}>
                <span className="font-bold text-[#D6EF4E]" style={{ fontSize: '0.875rem' }}>{currentUser?.initials}</span>
              </div>
              {!collapsed &&
                <div className="flex-1 min-w-0 text-left overflow-hidden">
                  <p className="font-oranienbaum text-white/90 truncate font-semibold leading-tight" style={{ fontSize: '0.8125rem' }}>{currentUser?.name}</p>
                  <p
                    className="text-white/50 truncate leading-tight transition-all duration-150"
                    style={{
                      fontSize: '0.7rem',
                      maxHeight: showEmail ? '1rem' : '0',
                      opacity: showEmail ? 1 : 0,
                      overflow: 'hidden',
                    }}>
                    {currentUser?.email}
                  </p>
                </div>
              }
            </button>

            {userMenuOpen &&
              <div className="absolute bottom-full left-0 mb-2 w-64 bg-[#1A1A1A] rounded-xl shadow-2xl border border-white/10 overflow-hidden z-[1100]">
                <div className="p-4 border-b border-white/10">
                  <p className="font-oranienbaum text-white/90 font-semibold truncate" style={{ fontSize: 'var(--fs-base)' }}>{currentUser?.name}</p>
                  <p className="text-white/50 truncate" style={{ fontSize: 'var(--fs-sm)' }}>{currentUser?.email}</p>
                </div>
                <div className="p-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-oranienbaum text-red-400 hover:bg-red-500/10 transition-colors text-left"
                    style={{ fontSize: 'var(--fs-sm)' }}>
                    <Icon name="LogOut" size={18} />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;